import React, { useState, useEffect } from 'react'
import { ServerIcon, MessageSquareIcon } from '@bizfirst/common-react'

export interface ChatGroup {
  serverId: string
  groupName: string
}

export interface GroupInfo {
  groupId: string
  groupName: string
  serverId: string
  memberCount: number
  createdAt: string
  description?: string
  createdBy?: string
  lastActivityAt?: string
  members?: string[]
}

export interface ServerWithGroups {
  serverId: string
  serverName: string
  serverDescription?: string
  groups: GroupInfo[]
  isExpanded: boolean
}

export interface ServerGroupTreePanelProps {
  /** List of servers with their groups */
  servers: ServerWithGroups[]
  /** Currently selected server ID */
  selectedServerId: string | null
  /** Currently selected group */
  selectedGroup: ChatGroup | null
  /** Loading state */
  loading?: boolean
  /** Callback when server is selected */
  onSelectServer: (serverId: string) => void
  /** Callback when group is selected */
  onSelectGroup: (serverId: string, group: GroupInfo) => void
  /** Custom class name */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
}

const EXPANDED_SERVERS_KEY = 'server-group-tree-expanded-servers'

function getExpandedServers(): Set<string> {
  try {
    const stored = localStorage.getItem(EXPANDED_SERVERS_KEY)
    return stored ? new Set(JSON.parse(stored)) : new Set()
  } catch {
    return new Set()
  }
}

function saveExpandedServers(servers: ServerWithGroups[]) {
  const expanded = servers.filter((s) => s.isExpanded).map((s) => s.serverId)
  localStorage.setItem(EXPANDED_SERVERS_KEY, JSON.stringify(expanded))
}

/**
 * ServerGroupTreePanel - Reusable tree navigation for server/group selection
 *
 * Features:
 * - Expandable/collapsible server tree
 * - Group listing under each server
 * - Selection highlighting for servers and groups
 * - localStorage persistence for expanded state
 * - Responsive with custom styling support
 *
 * Usage:
 * ```tsx
 * import { ServerGroupTreePanel, type ServerWithGroups, type ChatGroup } from '@edge-stream/app-ui-components-react'
 *
 * const [servers, setServers] = useState<ServerWithGroups[]>([])
 * const [selectedServerId, setSelectedServerId] = useState<string | null>(null)
 * const [selectedGroup, setSelectedGroup] = useState<ChatGroup | null>(null)
 *
 * <ServerGroupTreePanel
 *   servers={servers}
 *   selectedServerId={selectedServerId}
 *   selectedGroup={selectedGroup}
 *   onSelectServer={handleSelectServer}
 *   onSelectGroup={handleSelectGroup}
 * />
 * ```
 */
export function ServerGroupTreePanel({
  servers,
  selectedServerId,
  selectedGroup,
  loading = false,
  onSelectServer,
  onSelectGroup,
  className,
  style,
}: ServerGroupTreePanelProps): React.ReactElement {
  const [displayServers, setDisplayServers] = useState<ServerWithGroups[]>(servers)

  // Initialize expanded state from localStorage on mount
  useEffect(() => {
    const expandedSet = getExpandedServers()
    const updated = servers.map((s) => ({
      ...s,
      isExpanded: expandedSet.has(s.serverId),
    }))
    setDisplayServers(updated)
  }, [servers.length]) // Only when server count changes

  // Update display servers when servers prop changes
  useEffect(() => {
    setDisplayServers(servers)
  }, [servers])

  const toggleServerExpanded = (serverId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const updated = displayServers.map((s) =>
      s.serverId === serverId ? { ...s, isExpanded: !s.isExpanded } : s,
    )
    setDisplayServers(updated)
    saveExpandedServers(updated)
  }

  const selectServer = (serverId: string) => {
    onSelectServer(serverId)
  }

  const selectGroup = (serverId: string, group: GroupInfo) => {
    onSelectGroup(serverId, group)
  }

  return (
    <div
      className={className}
      style={{
        width: '320px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        ...style,
      }}
    >
      {/* Header */}
      <div
        className="server-group-tree-panel__header"
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-tertiary)',
        }}
      >
        <h3
          style={{
            margin: 0,
            color: 'var(--primary)',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Servers
        </h3>
        <p
          style={{
            margin: '6px 0 0 0',
            color: 'var(--text-secondary)',
            fontSize: '12px',
          }}
        >
          {displayServers.length} {displayServers.length === 1 ? 'server' : 'servers'}
        </p>
      </div>

      {/* Tree View */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: '8px',
          gap: '2px',
        }}
      >
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'var(--text-secondary)',
            }}
          >
            <p>Loading...</p>
          </div>
        ) : displayServers.length === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              padding: '20px',
            }}
          >
            <p>No servers available</p>
          </div>
        ) : (
          displayServers.map((server) => (
            <div key={server.serverId} style={{ userSelect: 'none' }}>
              {/* Server Item */}
              <div
                onClick={() => selectServer(server.serverId)}
                style={{
                  padding: '10px 8px',
                  background:
                    selectedServerId === server.serverId && !selectedGroup
                      ? 'rgba(59, 130, 246, 0.15)'
                      : 'transparent',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                  border:
                    selectedServerId === server.serverId && !selectedGroup
                      ? '1px solid rgba(59, 130, 246, 0.4)'
                      : '1px solid transparent',
                  fontWeight: selectedServerId === server.serverId && !selectedGroup ? 600 : 500,
                }}
              >
                <button
                  onClick={(e) => toggleServerExpanded(server.serverId, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    color: 'var(--primary)',
                    minWidth: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease',
                    transform: server.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                  title={server.isExpanded ? 'Collapse' : 'Expand'}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                    }}
                  >
                    ›
                  </span>
                </button>

                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                    <ServerIcon size={16} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: selectedServerId === server.serverId && !selectedGroup ? 600 : 500,
                        fontSize: '13px',
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {server.serverName}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {server.groups.length} group{server.groups.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Groups (Nested) */}
              {server.isExpanded && (
                <div
                  style={{
                    marginLeft: '0',
                    marginTop: '2px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  {server.groups.length === 0 ? (
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        padding: '8px 16px',
                        fontStyle: 'italic',
                      }}
                    >
                      No groups
                    </div>
                  ) : (
                    server.groups.map((group) => {
                      const isGroupSelected =
                        selectedGroup?.serverId === server.serverId &&
                        selectedGroup?.groupName === group.groupName

                      return (
                        <div
                          key={`${server.serverId}-${group.groupName}`}
                          onClick={() => selectGroup(server.serverId, group)}
                          style={{
                            padding: '8px 8px 8px 28px',
                            background: isGroupSelected ? 'rgba(16, 185, 129, 0.12)' : 'transparent',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.15s ease',
                            border: isGroupSelected ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid transparent',
                            position: 'relative',
                            marginLeft: '4px',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: '20px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '1px',
                              height: '8px',
                              background: 'var(--border)',
                              opacity: 0.3,
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              left: '20px',
                              top: '50%',
                              width: '6px',
                              height: '1px',
                              background: 'var(--border)',
                              opacity: 0.3,
                            }}
                          />

                          <span
                            style={{
                              opacity: 0.8,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <MessageSquareIcon size={12} />
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: isGroupSelected ? 600 : 400,
                                fontSize: '12px',
                                color: isGroupSelected ? 'var(--primary)' : 'var(--text-primary)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {group.groupName}
                            </div>
                            <div
                              style={{
                                fontSize: '10px',
                                color: 'var(--text-secondary)',
                              }}
                            >
                              {group.memberCount || 0} member{(group.memberCount || 0) !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
